import { mutatePurchaseOrders } from "@/api/purchaseOrder/usePurchaseOrder";
import { users } from "@/api/users/useUsersQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { OrderProvider } from "@/features/order/context/OrderProvider";
import { OrderCreateItemsForm } from "@/features/order/form/CreateItemsForm";
import { OrderIncomingForm } from "@/features/order/form/OrderIncomingForm";
import { PurchaseOrderTableHead } from "@/features/purchaseOrder/PurchaseOrderTableHead";
import { PurchaseOrderTableInRowBody } from "@/features/purchaseOrder/PurchaseOrderTableInRowBody";
import { PurchaseOrderTableInRowHead } from "@/features/purchaseOrder/PurchaseOrderTableInRowHead";
import { PurchaseOrderTableRow } from "@/features/purchaseOrder/PurchaseOrderTableRow";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/Alert";
import { ConfirmForm } from "@/features/ui/confirm";
import { useLoader } from "@/hooks/useLoader";
import { handleErrorResponse } from "@/utils/errorMessage";
import { translateWithPartnerType } from "@/utils/partnerType";
import { Avatar, Button, Flex, Table, Text, Tooltip } from "@mantine/core";
import {
  PartnersGet200ResponseRowsInnerTypeEnum,
  PurchaseOrdersGet200Response,
  ScmApiPurchaseOrdersPurchaseOrderIdDeleteRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconFilterPlus, IconPlus, IconRecycle, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface OrderPageProps {
  children?: React.ReactNode;
}
export const OrderPage = ({ children }: OrderPageProps) => {
  const { t } = useTranslation();
  const { data: userInfo } = useQuery(users.who({}));
  const userCode = userInfo?.data?.code;
  const partnerType = userInfo?.data?.partner?.type;
  const { loading, LoadingOverlay, setLoading } = useLoader();
  const { openModal, closeModal } = useModal();
  const { query, setQuery, selectedRows, refetch, resetQueryStrings, data, setSelectedRows } =
    useStandardLayout();

  const [checkedMap, setCheckedMap] = useState<Map<number, Set<number>>>(new Map());

  const setData = (data as AxiosResponse<PurchaseOrdersGet200Response, any>)?.data?.rows
    ?.filter((item) => selectedRows.has(item.id))
    ?.map((item) => item);

  useEffect(() => {
    if (query.length === 0) {
      // query가 비어 있을 때만 실행
      setQuery([{ direction: { $eq: "OUTBOUND" } }]);
    }

    if (userInfo?.data.externalUserType) {
      setQuery([{ partnerCode: { $eq: userInfo?.data.partner?.code } }]);
    }
  }, [query]); // 의존성 배열 유지

  const { mutate: deleteMutate } = useMutation(
    (params: ScmApiPurchaseOrdersPurchaseOrderIdDeleteRequest) =>
      mutatePurchaseOrders
        .delete(params)
        .mutationFn(params as ScmApiPurchaseOrdersPurchaseOrderIdDeleteRequest | any),
    {
      onSuccess: () => {
        refetch();
        customAlert(
          t("message.deleteSuccess", {
            item: translateWithPartnerType(t, "order.order", partnerType),
          }),
          t("common.deleteSuccess"),
          "green",
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({
            error,
            errorContext: `${translateWithPartnerType(t, "order.order", partnerType)}${translateWithPartnerType(t, "order.id", partnerType)}`,
          }),
          t("common.deleteFail", {
            item: translateWithPartnerType(t, "order.order", partnerType),
          }),
          "red",
        );
      },
    },
  );

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert(
        t("message.deleteSelect", {
          item: translateWithPartnerType(t, "order.order", partnerType),
        }),
        t("common.deleteFail", {
          item: translateWithPartnerType(t, "order.order", partnerType),
        }),
        "red",
      );
      return;
    }

    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ purchaseOrderId: JSON.parse(row).id });
          });

          selectedRows.clear();
        }}
        noCallback={() => {}}
        commonCallback={() => closeModal(null)}
      />,
      null,
      `${t("common.delete", { item: translateWithPartnerType(t, "order.order", partnerType) })}`,
    );
  };

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <LoadingOverlay />
      <Flex p="sm" justify="space-between" rowGap="md">
        <Flex gap="sm">
          {partnerType === PartnersGet200ResponseRowsInnerTypeEnum.SALES ? null : (
            <Button
              leftIcon={<IconPlus />}
              onClick={() =>
                openModal(
                  <OrderCreateItemsForm />,
                  null,
                  t("common.additional", {
                    item: translateWithPartnerType(t, "orderItems.orderItems", partnerType),
                  }),
                  true,
                )
              }
            >
              {t("common.additional", {
                item: translateWithPartnerType(t, "order.both", partnerType),
              })}
            </Button>
          )}
          <Button
            leftIcon={<IconPlus />}
            color="orange"
            disabled={checkedMap.size == 0}
            onClick={() => {
              const selectedItemIds = Array.from(checkedMap.values()).flatMap((set) =>
                Array.from(set),
              );
              const dataItems =
                (data as AxiosResponse<PurchaseOrdersGet200Response, any>)?.data?.rows?.flatMap(
                  (row) =>
                    row.purchaseOrderItems?.filter((item) =>
                      selectedItemIds.includes(item.id ?? 0),
                    ),
                ) ?? [];
              openModal(
                <OrderProvider>
                  <OrderIncomingForm order={() => dataItems} setLoading={setLoading} />
                </OrderProvider>,
                null,
                partnerType === "PURCHASE" ? t("delivery.action") : t("inventory.incoming"),
              ).then((res) => {
                if (res) {
                  setSelectedRows((): Set<any> => new Set());
                }
              });
            }}
          >
            {partnerType === "PURCHASE" ? t("delivery.action") : t("inventory.incoming")}
          </Button>
        </Flex>
        <Flex gap="sm">
          <Button rightIcon={<IconRecycle />} color="teal" onClick={() => resetQueryStrings()}>
            {t("common.reset")}
          </Button>
          <Button rightIcon={<IconTrash />} color="red" onClick={() => handleDelete()}>
            {t("common.rowDelete")}
          </Button>
        </Flex>
      </Flex>
      {
        <Flex px="sm" direction="column" gap="xs">
          <Text fz="xl" fw={600}>
            {t("common.list", {
              item: translateWithPartnerType(t, "order.order", partnerType),
            })}
          </Text>
          <Text fz="xs">
            {t("common.subTitle", {
              item: translateWithPartnerType(t, "order.order", partnerType),
            })}
          </Text>
        </Flex>
      }
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <Table withColumnBorders withBorder>
            <PurchaseOrderTableHead pageKey="order" />
            <tbody>
              {(data as AxiosResponse<PurchaseOrdersGet200Response, any>)?.data?.rows?.map(
                (row, idx) => {
                  return (
                    <PurchaseOrderTableRow
                      pageKey="order"
                      key={idx}
                      rowData={row}
                      refetch={refetch}
                      checkedItemIds={checkedMap.get(row.id ?? 0) ?? new Set()}
                      onChangeCheckedItemIds={(updatedSet) => {
                        const childrenCount = row.purchaseOrderItems?.length;

                        setCheckedMap((prev) => {
                          const newMap = new Map(prev);

                          if (childrenCount === 0) {
                            return newMap;
                          }
                          if (updatedSet.size === 0) {
                            newMap.delete(row.id ?? 0);
                          } else {
                            newMap.set(row.id ?? 0, new Set(updatedSet));
                          }
                          return newMap;
                        });
                      }}
                    >
                      {({
                        purchaseOrderItems,
                        isAllChecked,
                        checkedItemIds,
                        handleToggleAll,
                        handleToggleItem,
                      }) => (
                        <tr>
                          <td colSpan={14}>
                            <Table withColumnBorders withBorder>
                              <PurchaseOrderTableInRowHead
                                pageKey="order"
                                key={row.id}
                                isAllChecked={isAllChecked}
                                handleToggleAll={handleToggleAll}
                              />
                              <tbody>
                                {purchaseOrderItems?.length && purchaseOrderItems?.length > 0 ? (
                                  (purchaseOrderItems ?? []).map((item, idex) => (
                                    <PurchaseOrderTableInRowBody
                                      pageKey="orderItems"
                                      item={item}
                                      idex={idex}
                                      checkedItemIds={checkedItemIds}
                                      handleToggleItem={handleToggleItem}
                                    />
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan={7} style={{ textAlign: "center" }}>
                                      {t("common.notFound")}
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </Table>
                          </td>
                        </tr>
                      )}
                    </PurchaseOrderTableRow>
                  );
                },
              )}
            </tbody>
          </Table>
        </StandardLayoutTableWrapper>
        <PaginationBox<PurchaseOrdersGet200Response> />
      </Flex>
    </Flex>
  );
};
